/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Checkbox, IconButton, Typography } from '@mui/material';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import '../responsiveTableStyles.css';
import {
  capitalizeFirstLetter,
  createPath,
  formatDate,
  secondsToSecondsAndMinutes,
  utcDateToLocalDateTime
} from '../../../utils/dynamicReportsUtils';
import { DynamicTableProps } from './dynamicTableProps';
import {
  TableContainer,
  NoDataContainer,
  HeaderContainer,
  IconContainer,
  TitleTableContainer,
  ValueTableContainer
} from './dynamicTableStyles';
import { ColumnItem } from '../../../types/dynamicReports/dynamicReportsInterface';
import { ModalJson } from '../modalJson/modalJson';
import { PopUpArrays } from '../popUpArrays/popUpArrays.component';
import { ModalLongString } from '../modalLongString/modalLongString';
import { useDynamicReportsConfig } from '../../../constants/context/dynamicReportsProvider/useDynamicReportsProvider';
import { DataType, directionSort } from '../../../constants/commonEnums';
import { HeaderDynamicReport } from '../../../constants/headerDynamicReport';
import { useSortableData } from '../../../hooks/useSortTableData';
import { useNavigate } from 'react-router-dom';

export const DynamicTable: React.FunctionComponent<DynamicTableProps> = ({
  columns,
  rows
}) => {
  const { configDynamicReports } = useDynamicReportsConfig();
  const { items, requestSort, sortConfig } = useSortableData(rows);
  const [limit, setLimit] = useState<number>(50);
  const [openModalJson, setOpenModalJson] = useState<boolean>(false);
  const [openArrayPopUp, setOpenArrayPopUp] = useState<boolean>(false);
  const [openModalLongString, setOpenModalLongString] =
    useState<boolean>(false);
  const [json, setJson] = useState<null | any>(null);
  const [array, setArray] = useState<null | any>(null);
  const [longString, setLongString] = useState<string>('');
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleCloseArraysPopUp = () => {
    setOpenArrayPopUp(false);
  };

  const handleCloseModalJson = () => {
    setOpenModalJson(false);
  };

  const handleCloseModalLongString = () => {
    setOpenModalLongString(false);
  };

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const isAtBottom =
      Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) <= 5;

    if (isAtBottom) {
      setLimit((prev) => prev + 10);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const getColumnSort = (name: string) => {
    if (!sortConfig) {
      return <ImportExportIcon />;
    }
    if (sortConfig.key === name) {
      if (sortConfig.direction === directionSort.ascending) {
        return <ArrowDownwardIcon />;
      }
      if (sortConfig.direction === directionSort.descending) {
        return <ArrowUpwardIcon />;
      }
    }
    return <ImportExportIcon />;
  };

  const filteredItems = items?.filter(
    (item: any) =>
      JSON.stringify(item)
        .toLowerCase()
        .indexOf(
          configDynamicReports.filterText
            ? configDynamicReports.filterText.toLowerCase()
            : ''
        ) !== -1
  );

  if (
    !columns ||
    !rows ||
    (columns && !columns.length) ||
    (rows && !rows.length)
  ) {
    return (
      <NoDataContainer>
        <Typography>No data...</Typography>
      </NoDataContainer>
    );
  }

  const renderDynamicTable = (columnsTable: ColumnItem[], rowsTable: any[]) =>
    rowsTable?.slice(0, limit).map((row, index) => (
      <Tr key={index}>
        {columnsTable.map((column: ColumnItem) => {
          if (column.dataType === DataType.DATE) {
            return (
              <Td key={column.name}>
                {utcDateToLocalDateTime(
                  row[capitalizeFirstLetter(column.name)]
                )}
              </Td>
            );
          }
          if (column.dataType === DataType.LONGSTRING) {
            return (
              <Td key={column.name}>
                <IconButton
                  onClick={() => {
                    setLongString(row[capitalizeFirstLetter(column.name)]);
                    setOpenModalLongString(true);
                  }}
                >
                  <OpenInBrowserIcon color="action" />
                </IconButton>
              </Td>
            );
          }
          if (
            capitalizeFirstLetter(column.name) ===
              HeaderDynamicReport.sunRiseDifference ||
            capitalizeFirstLetter(column.name) ===
              HeaderDynamicReport.sunSetDifference
          ) {
            return (
              <Td key={column.name}>
                {`${
                  secondsToSecondsAndMinutes(
                    row[capitalizeFirstLetter(column.name)]
                  )[0]
                }:${
                  secondsToSecondsAndMinutes(
                    row[capitalizeFirstLetter(column.name)]
                  )[1]
                }`}
              </Td>
            );
          }
          if (column.isLink && column.linkDataParameter) {
            return (
              <Td key={column.name}>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={createPath(
                    row[capitalizeFirstLetter(column.linkDataParameter)],
                    column.linkPath
                  )}
                >
                  {row[capitalizeFirstLetter(column.name)]}
                </a>
              </Td>
            );
          }

          if (
            !row[capitalizeFirstLetter(column.name)] &&
            column.dataType !== DataType.BOOLEAN &&
            row[capitalizeFirstLetter(column.name)] < 0
          ) {
            return <Td key={column.name}>N.A</Td>;
          }

          if (column.dataType === DataType.BOOLEAN) {
            return (
              <Td key={column.name}>
                <Checkbox
                  checked={row[capitalizeFirstLetter(column.name)]}
                  color="success"
                />
              </Td>
            );
          }
          if (column.dataType === DataType.DATETIME) {
            return (
              <Td key={column.name}>
                {formatDate(row[capitalizeFirstLetter(column.name)])}
              </Td>
            );
          }
          if (column.dataType === DataType.ARRAY) {
            return (
              <Td key={column.name}>
                <IconButton
                  onClick={() => {
                    setArray(row[capitalizeFirstLetter(column.name)]);
                    setOpenArrayPopUp(true);
                  }}
                >
                  <OpenInBrowserIcon color="action" />
                </IconButton>
              </Td>
            );
          }
          if (column.dataType === DataType.JSON) {
            return (
              <Td key={column.name}>
                <IconButton
                  onClick={() => {
                    setJson(row[capitalizeFirstLetter(column.name)]);
                    setOpenModalJson(true);
                  }}
                >
                  <OpenInBrowserIcon color="action" />
                </IconButton>
              </Td>
            );
          }
          if (column.dataType === DataType.BOOLALERT) {
            return (
              <Td key={column.name}>
                {row[capitalizeFirstLetter(column.name)] && (
                  <WarningAmberIcon color="warning" />
                )}
              </Td>
            );
          }
          return (
            <Td key={column.name}>
              <ValueTableContainer>
                {row[capitalizeFirstLetter(column.name)]}
              </ValueTableContainer>
            </Td>
          );
        })}
      </Tr>
    ));

  return (
    <>
      <div
        ref={containerRef}
        className="container-dynamic-table"
        style={{
          overflowX: 'auto', 
          overflowY: 'auto',
          height: 'calc(100vh - 100px)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <TableContainer
          className="dynamicTable-tableContainer"
          style={{
            width: '100%',
            height: '100%',
            overflow: 'visible'
          }}
        >
          <Table className="dynamicTable-responsiveTable">
            <Thead className="dynamicTable-thead">
              <Tr className="dynamicTable-tr">
                {columns &&
                  columns.map((column, index) => (
                    <Th
                      key={index}
                      className="dynamicTable-th"
                      onClick={() => {
                        if (
                          column.dataType === DataType.DATETIME ||
                          column.dataType === DataType.NUMBER ||
                          column.dataType === DataType.STRING
                        ) {
                          requestSort(capitalizeFirstLetter(column.name));
                        }
                      }}
                    >
                      <HeaderContainer className="dynamicTable-headerContainer">
                        <TitleTableContainer className="dynamicTable-titleTableContainer">
                          {column.displayName}
                        </TitleTableContainer>
                        <IconContainer className="dynamicTable-iconContainer">
                          {column.dataType === DataType.DATETIME ||
                          column.dataType === DataType.NUMBER ||
                          column.dataType === DataType.STRING
                            ? getColumnSort(capitalizeFirstLetter(column.name))
                            : null}
                        </IconContainer>
                      </HeaderContainer>
                    </Th>
                  ))}
              </Tr>
            </Thead>
            <Tbody className="dynamicTable-tbody">
              {renderDynamicTable(columns, filteredItems || [])}
            </Tbody>
          </Table>
        </TableContainer>
      </div>
      <ModalJson
        open={openModalJson}
        handleClose={handleCloseModalJson}
        json={json}
      />
      <PopUpArrays
        open={openArrayPopUp}
        handleClose={handleCloseArraysPopUp}
        array={array}
      />
      <ModalLongString
        open={openModalLongString}
        handleClose={handleCloseModalLongString}
        longString={longString}
      />
    </>
  );
};
